// F-MOB-C4 / MOB-W9: PWA glue.
//
// Two responsibilities:
//   1. registerServiceWorker(): register /service-worker.js if the browser
//      supports SW. Failures are swallowed — the page still works without SW.
//   2. attachInstallPrompt(): capture the BeforeInstallPromptEvent, show a
//      small floating "Install" button on mobile, and call prompt() when tapped.

import { isMobile } from './core/device';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  prompt(): Promise<void>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installButton: HTMLButtonElement | null = null;

/** Register the service worker. No-op if SW is not supported or document
 *  is unavailable (SSR / test environments). */
export function registerServiceWorker(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  // Defer until after page load so we don't compete with the initial paint.
  const register = () => {
    navigator.serviceWorker
      .register('/service-worker.js', { scope: '/' })
      .catch(() => {
        // Best-effort — page still works without a service worker.
      });
  };
  if (typeof window !== 'undefined' && document?.readyState === 'complete') {
    register();
  } else if (typeof window !== 'undefined') {
    window.addEventListener('load', register, { once: true });
  }
}

/** Capture beforeinstallprompt so we can prompt on user gesture later. */
export function attachInstallPrompt(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    // Hold off the auto prompt — we'll trigger it from the install button.
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    if (isMobile()) {
      showInstallButton();
    }
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    hideInstallButton();
  });
}

function showInstallButton(): void {
  if (installButton || typeof document === 'undefined') return;
  installButton = document.createElement('button');
  installButton.id = 'pwa-install-btn';
  installButton.type = 'button';
  installButton.textContent = 'Install';
  Object.assign(installButton.style, {
    position: 'fixed',
    bottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '8px 16px',
    background: 'rgba(20, 24, 20, 0.92)',
    border: '1px solid #5a6a3a',
    borderRadius: '4px',
    color: '#c8c090',
    fontFamily: 'Courier New, monospace',
    fontSize: '12px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    zIndex: '500',
  } satisfies Partial<CSSStyleDeclaration>);

  installButton.addEventListener('click', async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        deferredPrompt = null;
        hideInstallButton();
      }
    } catch {
      // User dismissed or browser blocked — leave the button visible so they
      // can retry later. deferredPrompt may now be invalid; clear it.
      deferredPrompt = null;
    }
  });

  document.body?.appendChild(installButton);
}

function hideInstallButton(): void {
  if (!installButton) return;
  installButton.remove();
  installButton = null;
}
